import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const NAV = [
    { to: "/shop", label: "Shop" },
    { to: "/category/new-in", label: "New In" },
    { to: "/collections", label: "Collections" },
    { to: "/about", label: "About Us" },
];

export default function Navbar() {
    const { user, logout } = useAuth();
    const { items } = useWishlist();
    const { count, setOpen: setCartOpen } = useCart();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState("");

    const submitSearch = (e) => {
        e.preventDefault();
        if (q.trim()) {
            navigate(`/search?q=${encodeURIComponent(q.trim())}`);
            setSearchOpen(false);
        }
    };

    return (
        <header className="bg-ivory-200/95 backdrop-blur-md sticky top-0 z-50 border-b border-sage-300/30" data-testid="site-navbar">
            <div className="relative flex w-full max-w-[1440px] mx-auto items-center justify-between min-h-[4.5rem] px-5 sm:px-8 lg:px-10 xl:px-14 py-3">
                {/* Left edge: mobile menu + desktop nav */}
                <div className="flex items-center gap-6 lg:gap-8 z-10 shrink-0">
                    <button className="lg:hidden text-ink-700" onClick={() => setOpen(true)} aria-label="Menu" data-testid="mobile-menu-btn">
                        <Menu size={22} strokeWidth={1.5} />
                    </button>
                    <nav className="hidden lg:flex items-center gap-6 xl:gap-9 text-xs uppercase tracking-[0.22em] text-ink-700">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
                                className={({ isActive }) =>
                                    `whitespace-nowrap hover:text-sage-600 transition-colors ${isActive ? "text-sage-700" : ""}`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>
                </div>

                {/* True viewport-center logo (independent of left/right width) */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
                    <div className="pointer-events-auto">
                        <Logo showTagline />
                    </div>
                </div>

                {/* Right edge: utility icons */}
                <div className="flex items-center gap-4 sm:gap-5 text-ink-700 z-10 shrink-0">
                    <button
                        onClick={() => setSearchOpen((s) => !s)}
                        className="hover:text-sage-600 transition-colors"
                        aria-label="Search"
                        data-testid="search-icon-btn"
                    >
                        <Search size={20} strokeWidth={1.5} />
                    </button>

                    <Link to="/wishlist" className="hover:text-sage-600 transition-colors relative" aria-label="Wishlist" data-testid="wishlist-icon-btn">
                        <Heart size={20} strokeWidth={1.5} />
                        {items.length > 0 && (
                            <span className="absolute -top-2 -right-2 bg-blush-400 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                                {items.length}
                            </span>
                        )}
                    </Link>

                    <button onClick={() => setCartOpen(true)} className="hover:text-sage-600 transition-colors relative" aria-label="Bag" data-testid="bag-icon-btn">
                        <ShoppingBag size={20} strokeWidth={1.5} />
                        {count > 0 && (
                            <span className="absolute -top-2 -right-2 bg-sage-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                                {count}
                            </span>
                        )}
                    </button>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="hover:text-sage-600 transition-colors" aria-label="Account" data-testid="user-icon-btn">
                                <User size={20} strokeWidth={1.5} />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            {user ? (
                                <>
                                    <DropdownMenuLabel className="font-serif text-base">Hi, {user.name?.split(" ")[0]}</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onSelect={() => navigate("/account")} data-testid="menu-account">
                                        <User size={16} className="mr-2" /> My Account
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onSelect={() => navigate("/wishlist")} data-testid="menu-wishlist">
                                        <Heart size={16} className="mr-2" /> My Wishlist
                                    </DropdownMenuItem>
                                    {user.role === "admin" && (
                                        <DropdownMenuItem onSelect={() => navigate("/admin")} data-testid="menu-admin">
                                            <LayoutDashboard size={16} className="mr-2" /> Admin Dashboard
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onSelect={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                                        <LogOut size={16} className="mr-2" /> Logout
                                    </DropdownMenuItem>
                                </>
                            ) : (
                                <>
                                    <DropdownMenuItem onSelect={() => navigate("/login")} data-testid="menu-login">Sign In</DropdownMenuItem>
                                    <DropdownMenuItem onSelect={() => navigate("/signup")} data-testid="menu-signup">Create Account</DropdownMenuItem>
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* Search drawer */}
            {searchOpen && (
                <div className="border-t border-sage-300/30 bg-ivory-100 animate-fade-up">
                    <form onSubmit={submitSearch} className="container-boutique py-5 flex gap-3 items-center" data-testid="search-form">
                        <Search size={18} strokeWidth={1.5} className="text-sage-600" />
                        <input
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search for dresses, sets, accessories…"
                            className="w-full bg-transparent border-none focus:outline-none text-ink-900 placeholder:text-ink-500 text-sm"
                            data-testid="search-input"
                        />
                        <button type="button" onClick={() => setSearchOpen(false)} className="text-ink-500 hover:text-ink-900">
                            <X size={18} />
                        </button>
                    </form>
                </div>
            )}

            {/* Mobile slide-over */}
            {open && (
                <div className="fixed inset-0 z-[60] lg:hidden">
                    <div className="absolute inset-0 bg-ink-900/40" onClick={() => setOpen(false)} />
                    <div className="absolute left-0 top-0 bottom-0 w-72 bg-ivory-100 p-6 animate-fade-up">
                        <div className="flex items-center justify-between mb-8">
                            <Logo size="sm" />
                            <button onClick={() => setOpen(false)} aria-label="Close" data-testid="mobile-close-btn">
                                <X size={22} />
                            </button>
                        </div>
                        <nav className="flex flex-col gap-5 text-sm uppercase tracking-[0.22em] text-ink-700">
                            {NAV.map((n) => (
                                <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="hover:text-sage-600" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}>
                                    {n.label}
                                </Link>
                            ))}
                            <Link to="/contact" onClick={() => setOpen(false)} className="hover:text-sage-600">Contact</Link>
                        </nav>
                    </div>
                </div>
            )}
        </header>
    );
}
